/**
 * Text Settings Popover
 *
 * Quick-access popover for text-related accessibility settings:
 * - Font Size
 * - Line Spacing
 * - Dyslexia Font
 */

import { useState, useEffect } from 'react'
import { Type, RotateCcw } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Label } from '@/components/ui/label'
import { Switch } from '@/components/ui/switch'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import {
  getUserAccessibilitySettings,
  saveUserAccessibilitySettings,
  applyUserAccessibilitySettings,
  type UserAccessibilitySettings,
} from '@lina/accessibility/user'

interface TextSettingsPopoverProps {
  open?: boolean
  onOpenChange?: (open: boolean) => void
  children?: React.ReactNode
}

export function TextSettingsPopover({ open, onOpenChange, children }: TextSettingsPopoverProps) {
  const [settings, setSettings] = useState<UserAccessibilitySettings>(() =>
    getUserAccessibilitySettings(),
  )

  // Update DOM when settings change
  useEffect(() => {
    applyUserAccessibilitySettings(settings)
    saveUserAccessibilitySettings(settings)
  }, [settings])

  const updateSetting = <K extends keyof UserAccessibilitySettings>(
    key: K,
    value: UserAccessibilitySettings[K],
  ) => {
    setSettings((prev) => ({ ...prev, [key]: value }))
  }

  const handleReset = () => {
    const defaults = {
      fontSize: 'medium' as const,
      lineSpacing: 'normal' as const,
      dyslexiaFont: false,
    }
    setSettings((prev) => ({ ...prev, ...defaults }))
  }

  return (
    <Popover open={open} onOpenChange={onOpenChange}>
      <PopoverTrigger asChild>
        {children || (
          <Button variant="ghost" size="icon" className="h-8 w-8" title="Text settings">
            <Type className="w-4 h-4" />
          </Button>
        )}
      </PopoverTrigger>
      <PopoverContent className="w-80" align="center" side="top">
        <div className="space-y-4">
          {/* Header */}
          <div className="flex items-center justify-between">
            <div className="space-y-1">
              <h4 className="text-sm font-semibold">Text Settings</h4>
              <p className="text-xs text-muted-foreground">Customize text size and spacing</p>
            </div>
            <Button variant="ghost" size="icon" onClick={handleReset} title="Reset text settings">
              <RotateCcw className="w-4 h-4" />
            </Button>
          </div>

          <div className="border-t pt-3 space-y-4">
            {/* Font Size */}
            <div className="space-y-2">
              <Label htmlFor="text-font-size" className="text-xs font-medium">
                Font Size
              </Label>
              <Select
                value={settings.fontSize}
                onValueChange={(value) =>
                  updateSetting('fontSize', value as UserAccessibilitySettings['fontSize'])
                }
              >
                <SelectTrigger id="text-font-size" className="h-8 text-xs">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="small">Small (87.5%)</SelectItem>
                  <SelectItem value="medium">Default (100%)</SelectItem>
                  <SelectItem value="large">Large (112.5%)</SelectItem>
                  <SelectItem value="x-large">Extra Large (125%)</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Line Spacing */}
            <div className="space-y-2">
              <Label htmlFor="text-line-spacing" className="text-xs font-medium">
                Line Spacing
              </Label>
              <Select
                value={settings.lineSpacing}
                onValueChange={(value) =>
                  updateSetting('lineSpacing', value as UserAccessibilitySettings['lineSpacing'])
                }
              >
                <SelectTrigger id="text-line-spacing" className="h-8 text-xs">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="normal">Normal (1.5)</SelectItem>
                  <SelectItem value="comfortable">Comfortable (1.8)</SelectItem>
                  <SelectItem value="wide">Wide (2.0)</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Dyslexia Font */}
            <div className="space-y-3 border-t pt-3">
              <div className="flex items-center justify-between">
                <Label htmlFor="text-dyslexia" className="text-xs font-medium cursor-pointer">
                  Dyslexia Font
                </Label>
                <Switch
                  id="text-dyslexia"
                  checked={settings.dyslexiaFont}
                  onCheckedChange={(checked) => updateSetting('dyslexiaFont', checked)}
                />
              </div>
              <p className="text-xs text-muted-foreground">
                Uses Lexend font with increased spacing for better readability
              </p>
            </div>
          </div>
        </div>
      </PopoverContent>
    </Popover>
  )
}
